import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useDateFormat } from '@/composables/dateFormat';
import { useStore } from 'vuex';
import { ProjectGetter } from '@/store/project/project.module';
import { Project } from '@/store/project/project.model';
import { ProcureGetter } from '@/store/procure/procure.module';
import { Procure } from '@/store/procure/procure.model';
import { ProgressAction, ProgressGetter } from '@/store/progress/progress.module';
import { Progress } from '@/store/progress/progress.model';
import { AccountGetter } from '@/store/account/account.module';
import { Role } from '@/store/role/role.model';
import ProjectProgressFormComponent from '../projectProgressForm/ProjectProgressForm.vue';
import ProjectReturnFormComponent from '../projectReturnForm/ProjectReturnForm.vue';
import ProjectProgressAttachmentOffcanvasComponent from '../projectProgressAttachmentOffcanvas/ProjectProgressAttachmentOffcanvas.vue';
import ProjectProgressAttachmentModalComponent from '../projectProgressAttachmentModal/ProjectProgressAttachmentModal.vue';
import ProjectProgressAttachmentFormComponent from '../projectProgressAttachmentForm/ProjectProgressAttachmentForm.vue';
import ProjectProgressImageModalComponent from '../projectProgressImageModal/ProjectProgressImageModal.vue';

export default defineComponent({
  name: 'ProjectProgressComponent',
  components: {
    ProjectProgressFormComponent,
    ProjectReturnFormComponent,
    ProjectProgressAttachmentOffcanvasComponent,
    ProjectProgressAttachmentModalComponent,
    ProjectProgressAttachmentFormComponent,
    ProjectProgressImageModalComponent,
  },
  props: {
    projectId: {
      type: Number
    }
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const swal = useSweetAlert();
    const dateFormat = useDateFormat();
    const store = useStore();
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const canEdit = computed(() => role.value?.name === 'เจ้าหน้าที่ ส.กกภ.' || role.value?.name === 'เจ้าหน้าที่ประจำจังหวัด');
    const project = computed<Project>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const procure = computed<Procure | null>(() => store.getters[ProcureGetter.GET]);
    const progresses = computed<Progress[]>(() => store.getters[ProgressGetter.ALL]);
    const isCanceled = computed(() => project.value?.isCanceled);
    const isProcured = computed(() => project.value?.projectStatus.key === 2);
    const isProgress = computed(() => project.value?.projectStatus.key > 2);
    const isSpent = computed(() => project.value?.projectStatus.key === 5);
    const isCompleted = computed(() => project.value?.projectStatus.key >= 5);
    const isFinished = computed(() => project.value?.isFinished);
    const selectedProgressId = ref<number | null>();
    const progressForm = ref<InstanceType<typeof ProjectProgressFormComponent>>();
    const returnForm = ref<InstanceType<typeof ProjectReturnFormComponent>>();
    const attachmentOffcanvas = ref<InstanceType<typeof ProjectProgressAttachmentOffcanvasComponent>>();
    const attachmentModal = ref<InstanceType<typeof ProjectProgressAttachmentModalComponent>>();
    const attachmentForm = ref<InstanceType<typeof ProjectProgressAttachmentFormComponent>>();
    const imageModal = ref<InstanceType<typeof ProjectProgressImageModalComponent>>();

    onMounted(() => {
      if (props.projectId) {
        store.dispatch(ProgressAction.FETCH, { projectId: props.projectId });
      }
    });

    watch(() => project.value?.projectStatus.key, () => {
      store.dispatch(ProgressAction.FETCH, { projectId: project.value?.id });
    });

    const progressClick = () => {
      Promise.resolve(selectedProgressId.value = null)
        .then(() => progressForm.value?.show());
    };

    const returnClick = () => {
      Promise.resolve(selectedProgressId.value = null)
        .then(() => returnForm.value?.show());
    };

    const editClick = (progressId: number, spentTypeTd: number) => {
      if (spentTypeTd === 1) {
        Promise.resolve(selectedProgressId.value = progressId)
          .then(() => progressForm.value?.show());
      } else {
        Promise.resolve(selectedProgressId.value = progressId)
          .then(() => returnForm.value?.show());
      }
    };

    const attachmentClick = (progressId: number) => {
      Promise.resolve(selectedProgressId.value = progressId)
        .then(() => attachmentModal.value?.show());
    };

    const uploadClick = (progressId: number) => {
      Promise.resolve(selectedProgressId.value = progressId)
        .then(() => attachmentModal.value?.hide())
        .then(() => attachmentForm.value?.show());
    };

    const formHidden = (progressId: number) => {
      Promise.resolve(selectedProgressId.value = progressId)
        .then(() => attachmentForm.value?.hide())
        .then(() => attachmentModal.value?.show());
    };

    const imageClick = (progressId: number) => {
      Promise.resolve(selectedProgressId.value = progressId)
        .then(() => imageModal.value?.show());
    };

    const deleteClick = (progressId: number) => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ProgressAction.DELETE, { projectId: props.projectId, id: progressId })
              .then(() => context.emit('refresh'));
          }
        });
    };

    const showAttachment = (progressId: number) => {
      Promise.resolve(selectedProgressId.value = progressId)
        .then(() => attachmentOffcanvas.value?.show());
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'short');

    const refresh = () => {
      context.emit('refresh');
    };

    return {
      progressForm,
      returnForm,
      attachmentOffcanvas,
      attachmentModal,
      attachmentForm,
      imageModal,
      project,
      procure,
      progresses,
      canEdit,
      isCanceled,
      isProcured,
      isProgress,
      isSpent,
      isCompleted,
      isFinished,
      selectedProgressId,
      progressClick,
      uploadClick,
      formHidden,
      imageClick,
      returnClick,
      showAttachment,
      editClick,
      attachmentClick,
      deleteClick,
      thaiDate,
      refresh,
    };
  },
});
